import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, CalendarIcon, ClipboardList } from 'lucide-react';
import { CompanyCombobox } from '@/components/insurance/CompanyCombobox';
import { EmployeeCombobox } from '@/components/insurance/EmployeeCombobox';
import { formatDateStr, parseDateStr, formatToISO } from '@/lib/insurance/dateUtils';
import { cn } from '@/lib/utils';
import { FieldRenderer } from '@/components/insurance/policy-application/FieldRenderer';
import type { IPolicyInformationCardProps } from '@/@types/insurance';
import type { IFormField } from '@/@types/policyFormTemplates';
import type { Id } from '@convex/_generated/dataModel';

// LOCAL_TYPE_JUSTIFIED: date picker field is an internal layout primitive used only within this card — not a domain type
interface IDatePickerFieldProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
}

function DatePickerField({ label, value, onChange, disabled }: IDatePickerFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-foreground">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal !h-10',
              !value && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? formatDateStr(value) : 'Select date...'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value ? parseDateStr(value) : undefined}
            onSelect={d => onChange(d ? formatToISO(d) : '')}
            captionLayout="dropdown"
            startMonth={new Date(2020, 0)}
            endMonth={new Date(2040, 11)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function PolicyInformationCard({
  isEditing,
  isSubmitting,
  typeList,
  selectedTypeId,
  onTypeChange,
  selectedTypeName,
  providerList,
  providerFieldValue,
  onProviderChange,
  policyNumber,
  onPolicyNumberChange,
  policyStartDate,
  onPolicyStartDateChange,
  policyExpireDate,
  onPolicyExpireDateChange,
  policyDuration,
  onPolicyDurationChange,
  employeePolicyForm,
  onEmployeeFormChange,
  benefitPlans,
  selectedBenefitPlanId,
  onBenefitPlanChange,
  policyInfoFields,
  policyInfoValues,
  onPolicyInfoChange,
}: IPolicyInformationCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5">
      <div className="flex items-center gap-2.5">
        <ClipboardList className="w-5 h-5 text-lime-700 dark:text-lime-400" />
        <h2 className="text-base font-semibold text-foreground">Policy Information</h2>
      </div>
      <div className="h-px bg-border" />

      {/* Row 1: Insurance Type + Provider + Policy Number */}
      <div className="grid grid-cols-3 gap-5">
        {!isEditing ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-foreground">
              Insurance Type <span className="text-destructive">*</span>
            </label>
            <Select
              value={selectedTypeId ?? ''}
              onValueChange={val => onTypeChange(val as Id<'orgInsuranceTypes'>)} // AS_JUSTIFIED: value comes from Select populated with IOrgInsuranceType IDs
            >
              <SelectTrigger className="w-full !h-10">
                <SelectValue placeholder="Select an insurance type" />
              </SelectTrigger>
              <SelectContent>
                {typeList.map(t => (
                  <SelectItem key={t._id} value={t._id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-foreground">Insurance Type</label>
            <Input value={selectedTypeName ?? ''} disabled className="!h-10" />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Provider</label>
          {providerList.length === 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open('/insurance/providers/new', '_blank')}
              disabled={isSubmitting}
              className="self-start"
            >
              <Building2 className="w-4 h-4 mr-1.5" />
              Create Provider
            </Button>
          ) : (
            <Select
              value={providerFieldValue}
              onValueChange={onProviderChange}
              disabled={isSubmitting || !selectedTypeId}
            >
              <SelectTrigger className="w-full !h-10">
                <SelectValue
                  placeholder={selectedTypeId ? 'Select provider' : 'Select type first'}
                />
              </SelectTrigger>
              <SelectContent>
                {providerList.map(p => (
                  <SelectItem key={p._id} value={p._id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">
            Policy Number <span className="text-destructive">*</span>
          </label>
          <Input
            value={policyNumber}
            onChange={e => onPolicyNumberChange(e.target.value)}
            placeholder="e.g. POL-2024-001"
            disabled={isSubmitting}
            className="!h-10"
          />
        </div>
      </div>

      {/* Row 2: Dates + Duration */}
      <div className="grid grid-cols-3 gap-5">
        <DatePickerField
          label="Policy Start Date"
          value={policyStartDate}
          onChange={onPolicyStartDateChange}
          disabled={isSubmitting}
        />
        <DatePickerField
          label="Policy Expire Date"
          value={policyExpireDate}
          onChange={onPolicyExpireDateChange}
          disabled={isSubmitting}
        />
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Policy Duration</label>
          <Select
            value={policyDuration}
            onValueChange={onPolicyDurationChange}
            disabled={isSubmitting}
          >
            <SelectTrigger className="w-full !h-10">
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3_months">3 months</SelectItem>
              <SelectItem value="6_months">6 months</SelectItem>
              <SelectItem value="12_months">12 months</SelectItem>
              <SelectItem value="24_months">24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 3: Source + Company + Employee */}
      <div className="grid grid-cols-3 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Source</label>
          <Select
            value={employeePolicyForm.policySource}
            onValueChange={val => onEmployeeFormChange.onSourceChange(val)}
            disabled={isSubmitting}
          >
            <SelectTrigger className="w-full !h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">Personal</SelectItem>
              <SelectItem value="company_employer">Company Employer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {employeePolicyForm.policySource === 'company_employer' && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-foreground">Company</label>
            <CompanyCombobox
              companies={employeePolicyForm.companies}
              value={employeePolicyForm.selectedCompanyId}
              onChange={companyId => onEmployeeFormChange.onCompanyChange(companyId)}
              disabled={
                isSubmitting ||
                (employeePolicyForm.isPrefilledFromUrl &&
                  Boolean(employeePolicyForm.selectedCompanyId))
              }
            />
          </div>
        )}
        {employeePolicyForm.policySource === 'company_employer' &&
          employeePolicyForm.selectedCompanyId && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[13px] font-medium text-foreground">Employee</label>
              <EmployeeCombobox
                employees={employeePolicyForm.employees}
                value={employeePolicyForm.selectedEmployeeId}
                onChange={id => onEmployeeFormChange.onEmployeeChange(id)}
                disabled={
                  isSubmitting ||
                  (employeePolicyForm.isPrefilledFromUrl &&
                    Boolean(employeePolicyForm.selectedEmployeeId))
                }
              />
            </div>
          )}
      </div>

      {/* Row 4: Plan selector (shown when benefit plans available for selected provider + type) */}
      {benefitPlans && benefitPlans.length > 0 && onBenefitPlanChange && (
        <div className="grid grid-cols-3 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-foreground">Plan</label>
            <Select
              value={selectedBenefitPlanId ?? ''}
              onValueChange={onBenefitPlanChange}
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full !h-10">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {benefitPlans.map(p => (
                  <SelectItem key={p._id} value={p._id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Row 5: Provider-specific policy info fields (area of coverage, etc.) */}
      {policyInfoFields && policyInfoFields.length > 0 && (
        <div className="grid grid-cols-3 gap-5">
          {policyInfoFields.map((field: IFormField) => (
            <FieldRenderer
              key={field.key}
              field={field}
              value={policyInfoValues?.[field.key]}
              onChange={onPolicyInfoChange ?? (() => undefined)}
              allValues={policyInfoValues ?? {}}
              disabled={isSubmitting}
            />
          ))}
        </div>
      )}
    </div>
  );
}
